import { type FocusEvent, memo, useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { PlainTextCustomFieldFilter } from '~/components/Filters/CustomFieldFilter/PlainTextCustomFieldFilter';
import { setPlainTextCustomFieldFilterAction } from '~/store/filters/actions';
import { plainTextCustomFieldFilterSelector } from '~/store/filters/selectors';
import {
  type PlainTextCustomFieldFilter as PlainTextCustomFieldFilterState,
  type PlainTextCustomFieldOperator,
} from '~/store/filters/types';
import {
  normalizePlainTextCustomFieldValue,
  resolvePlainTextOperatorChange,
  SEARCH_ASSETS_MAX_CUSTOM_FIELD_VALUES,
  validatePlainTextCustomFieldValue,
} from '~/utils/filters/plainTextCustomFieldFilter';
import { useAirSelector } from '~/utils/ReduxUtils';

export type PlainTextCustomFieldFilterControlsProps = {
  customFieldId: string;
  fieldName: string;
  onCommitted?: () => void;
};

export const PlainTextCustomFieldFilterControls = memo(
  ({ customFieldId, fieldName, onCommitted }: PlainTextCustomFieldFilterControlsProps) => {
    const dispatch = useDispatch();
    const containerRef = useRef<HTMLDivElement>(null);
    const plainText = useAirSelector((state) => plainTextCustomFieldFilterSelector(state, customFieldId));
    const operator = plainText?.operator ?? 'is';
    const values = plainText?.values ?? [];
    const isSingleValueOperator = operator === 'is' || operator === 'prefix';
    const [editedValue, setEditedValue] = useState<string | undefined>(undefined);
    const inputValue = isSingleValueOperator ? (editedValue ?? values[0] ?? '') : (editedValue ?? '');
    const [error, setError] = useState<ReturnType<typeof validatePlainTextCustomFieldValue>>(null);

    const commit = useCallback(
      (next: PlainTextCustomFieldFilterState, shouldApply: boolean) => {
        dispatch(setPlainTextCustomFieldFilterAction({ customFieldId, plainText: next }));
        if (shouldApply) {
          onCommitted?.();
        }
      },
      [customFieldId, dispatch, onCommitted],
    );

    const onOperatorChange = useCallback(
      (nextOperator: PlainTextCustomFieldOperator) => {
        const result = resolvePlainTextOperatorChange({
          editedValue,
          nextOperator,
          operator,
          values,
        });

        if (result.type === 'error') {
          setError(result.error);
          return;
        }

        setEditedValue(undefined);
        setError(null);
        commit(result.plainText, result.shouldApply);
      },
      [commit, editedValue, operator, values],
    );

    const onCommitDraft = useCallback(
      ({ showEmptyError }: { showEmptyError: boolean }) => {
        if (editedValue === undefined) {
          if (showEmptyError && isSingleValueOperator && values.length === 0) {
            setError('empty');
          }

          return;
        }

        const validationError = validatePlainTextCustomFieldValue(editedValue);

        if (validationError === 'empty') {
          if (showEmptyError) {
            setError('empty');
            return;
          }

          setEditedValue(undefined);
          setError(null);
          return;
        }
        if (validationError) {
          setError(validationError);
          return;
        }

        const normalized = normalizePlainTextCustomFieldValue(editedValue);

        if (isSingleValueOperator) {
          setEditedValue(undefined);
          setError(null);
          commit({ operator, values: [normalized] }, true);
          return;
        }

        if (operator !== 'or' && operator !== 'not') {
          return;
        }
        if (values.includes(normalized)) {
          setEditedValue(undefined);
          setError(null);
          return;
        }
        if (values.length >= SEARCH_ASSETS_MAX_CUSTOM_FIELD_VALUES) {
          setError('tooMany');
          return;
        }

        setEditedValue(undefined);
        setError(null);
        commit({ operator, values: [...values, normalized] }, true);
      },
      [commit, editedValue, isSingleValueOperator, operator, values],
    );

    const onDraftBlur = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        const nextTarget = event.relatedTarget;

        if (nextTarget instanceof Node && containerRef.current?.contains(nextTarget)) {
          return;
        }
        onCommitDraft({ showEmptyError: false });
      },
      [onCommitDraft],
    );

    const onDraftValueChange = useCallback((value: string) => {
      setEditedValue(value);
      setError(null);
    }, []);

    const onRemoveValue = useCallback(
      (value: string) => {
        commit(
          {
            operator,
            values: values.filter((current) => current !== value),
          },
          true,
        );
      },
      [commit, operator, values],
    );

    return (
      <div ref={containerRef}>
        <PlainTextCustomFieldFilter
          draftValue={inputValue}
          error={error}
          fieldName={fieldName}
          onCommitDraft={onCommitDraft}
          onDraftBlur={onDraftBlur}
          onDraftValueChange={onDraftValueChange}
          onOperatorChange={onOperatorChange}
          onRemoveValue={onRemoveValue}
          operator={operator}
          values={values}
        />
      </div>
    );
  },
);

PlainTextCustomFieldFilterControls.displayName = 'PlainTextCustomFieldFilterControls';
